import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Box, Divider, TextButton, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';
import BillingConstants from '../../constants/BillingConstants';
import { BillingInvoicePage } from '../BillingInvoicePage';
import type { ColumnConfig } from '../BillingInvoicePage';
import BillingTopBanner from '../../components/BillingTopBanner';
import { numberUtils, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import PropertyNameList from '../../components/PropertyNameList';
import { type AdproductsZuoraBillingInvoiceDto, ZuoraInvoiceType } from '@zg-rentals/ts-adproducts-client';
import { EVENT_IDS } from '../../model/ClickstreamEvents';
import { localDateToDate } from '../../utils/dateUtils';
import billingUtils from '../../utils/billingUtils';
import { PremiumPaymentsTable } from '../PremiumReceiptsPage';
import type { InvoiceListFilterConfig } from '../../hooks/useInvoiceList';

type PaidView = 'invoices' | 'premium';

const invoiceRenderer = (invoice: AdproductsZuoraBillingInvoiceDto) =>
  invoice.invoiceType !== ZuoraInvoiceType.NUCLEUS_LISTING ? (
    <Link to={`/rental-manager/billing/invoice/${invoice.invoiceNumber}?prev=paid`}>
      <TextButton
        data-eventid={EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE}
        data-eventdescription="View billing invoice page event"
      >
        {invoice.invoiceNumber}
      </TextButton>
    </Link>
  ) : (
    <Box css={{ wordBreak: 'break-all' }}>{invoice.invoiceNumber}</Box>
  );

const fullColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>> = [
  {
    dataKey: 'invoiceLocalDate',
    width: 1,
    header: 'Date',
    align: 'left',
    renderer: (invoice) =>
      invoice.invoiceLocalDate ? format(localDateToDate(invoice.invoiceLocalDate), 'MM/dd/yy') : '-',
  },
  {
    width: 2,
    align: 'left',
    header: 'Invoice No.',
    dataKey: 'invoiceNumber',
    renderer: invoiceRenderer,
    className: 'TableMFH-data-word-break',
  },
  {
    width: 3,
    align: 'left',
    header: 'Properties',
    dataKey: 'propertyNames',
    renderer: (invoice) => <PropertyNameList invoice={invoice} />,
  },
  {
    width: 2,
    align: 'left',
    header: 'Account Number',
    dataKey: 'accountNumber',
  },
  {
    width: 2,
    header: 'Amount',
    dataKey: 'amount',
    renderer: (invoice) => numberUtils.asCurrency(invoice.amount),
    align: 'right',
  },
  {
    width: 1,
    header: 'Status',
    dataKey: 'balance',
    renderer: (invoice) => (invoice.balance === 0 ? 'Paid' : 'Partially Paid'),
    align: 'center',
  },
];

const mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>> = [
  {
    dataKey: 'invoiceLocalDate',
    width: 2,
    header: 'Date',
    align: 'left',
    renderer: (invoice) => invoice.invoiceLocalDate && format(localDateToDate(invoice.invoiceLocalDate), 'MM/dd/yy'),
  },
  {
    width: 4,
    header: 'Invoice',
    dataKey: 'invoiceNumber',
    align: 'left',
    renderer: (invoice) => (
      <>
        {invoiceRenderer(invoice)}
        {invoice.invoiceType === ZuoraInvoiceType.NUCLEUS_LISTING && <Divider />}
        <div>
          <PropertyNameList invoice={invoice} />
        </div>
        <div>{invoice.accountNumber}</div>
      </>
    ),
  },
  {
    width: 2,
    header: 'Amount',
    dataKey: 'amount',
    renderer: (invoice) => (
      <>
        <div>{numberUtils.asCurrency(invoice.amount)}</div>
        <div>{invoice.balance === 0 ? 'Paid' : 'Partially Paid'}</div>
      </>
    ),
    align: 'right',
  },
];

const PaidInvoicesPage = () => {
  const { user } = useRentalManagerUser();
  const isNucleusOnly = billingUtils.isOnlyNucleusUser(user);
  const showToggle = billingUtils.hasNucleusRole(user) && !isNucleusOnly;
  const [activeView, setActiveView] = useState<PaidView>('invoices');
  const [premiumFilters, setPremiumFilters] = useState<InvoiceListFilterConfig>({
    property: [],
    city: [],
    state: [],
  });

  const handleViewChange = (value: string | Array<string>) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (next === 'invoices' || next === 'premium') {
      setActiveView(next);
    }
  };

  if (isNucleusOnly) {
    return (
      <>
        <BillingTopBanner filters={premiumFilters} setFilters={setPremiumFilters} />
        <PremiumPaymentsTable startDate={premiumFilters.startDate} endDate={premiumFilters.endDate} />
      </>
    );
  }

  if (!showToggle) {
    return (
      <BillingInvoicePage
        fullColumnConfig={fullColumnConfig}
        mobileColumnConfig={mobileColumnConfig}
        title="Paid Invoices"
        emptyMessage={BillingConstants.EMPTY_NOTIFICATIONS.NO_PAID_INVOICES}
        showHistory={true}
        isPaymentDue={false}
      />
    );
  }

  const toggle = (
    <ToggleButtonGroup aria-label="Toggle payment view" value={activeView} onValueChange={handleViewChange}>
      <ToggleButton value="invoices">Invoices</ToggleButton>
      <ToggleButton value="premium">Premium payments</ToggleButton>
    </ToggleButtonGroup>
  );

  if (activeView === 'premium') {
    return (
      <>
        <BillingTopBanner headerLeft={toggle} filters={premiumFilters} setFilters={setPremiumFilters} />
        <PremiumPaymentsTable startDate={premiumFilters.startDate} endDate={premiumFilters.endDate} />
      </>
    );
  }

  return (
    <BillingInvoicePage
      excludeNucleus
      fullColumnConfig={fullColumnConfig}
      headerLeft={toggle}
      mobileColumnConfig={mobileColumnConfig}
      title="Paid Invoices"
      emptyMessage={BillingConstants.EMPTY_NOTIFICATIONS.NO_PAID_INVOICES}
      showHistory={true}
      isPaymentDue={false}
    />
  );
};

export default PaidInvoicesPage;
